import type { HTMLAttributes, KeyboardEvent, MouseEventHandler, ReactNode, Ref } from 'react';

export interface InteractiveSurfaceProps extends HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  ref?: Ref<HTMLDivElement>;
}

const ACTIVATION_KEYS = new Set(['Enter', ' ']);

export function InteractiveSurface(props: InteractiveSurfaceProps): ReactNode {
  const {
    disabled = false,
    ref,
    role = 'button',
    tabIndex,
    onClick,
    onKeyDown,
    ...rest
  } = props;

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    onKeyDown?.(event);
    if (
      disabled ||
      event.defaultPrevented ||
      event.currentTarget !== event.target ||
      onClick === undefined ||
      !ACTIVATION_KEYS.has(event.key)
    ) {
      return;
    }
    event.preventDefault();
    event.currentTarget.click();
  };

  return (
    <div
      {...rest}
      ref={ref}
      role={role}
      tabIndex={disabled ? undefined : (tabIndex ?? 0)}
      aria-disabled={disabled ? true : undefined}
      data-component="InteractiveSurface"
      data-disabled={disabled ? 'true' : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  );
}
